import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Color from 'color';

import { accountService, encryptService } from '@/lib/services';
import { createUseThemedStyles, useAccount, useHandleError, useNavigationConfig } from '@/hooks';
import { HeaderDesktop, HeaderMenu, HeaderMobile, NavigationMain, WelcomeBanner } from '@/components';
import { BannerMessage } from '@/lib/models';
import { mediaQueries } from '@/jss';
import { useLocation } from 'react-router-dom';
import { constructUrl } from '@/lib/utils';

const useStyles = createUseThemedStyles((theme) => ({
	header: {
		top: 0,
		left: 0,
		right: 0,
		zIndex: 10,
		position: 'fixed',
		backgroundColor: theme.colors.primary,
		backgroundImage: `linear-gradient(-45deg, ${Color(theme.colors.primary)
			.darken(0.4)
			.hex()} 0%, ${Color(theme.colors.primary)
			.lighten(0.2)
			.desaturate(0.2)
			.hex()} 100%)`,
	},
	headerDesktop: {
		display: 'none',
		[mediaQueries.nav]: {
			display: 'flex',
		},
	},
	headerMobile: {
		[mediaQueries.nav]: {
			display: 'none',
		},
	},
}));

export type NavigationLinks = Record<NAVIGATION_SECTION, NaviationSectionLink[]>;

enum NAVIGATION_SECTION {
	MAIN = 'MAIN',
	CLIENT_MANAGEMENT = 'CLIENT_MANAGEMENT',
	SPEND_MATTERS = 'SPEND_MATTERS',
	ACCOUNT = 'ACCOUNT',
}

export interface NaviationSectionLink {
	to: string;
	title: string;
	description?: string;
	mobileTitle?: string;
	external?: boolean;
	onClick?(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void;
}

export const Header = () => {
	const handleError = useHandleError();
	const classes = useStyles();
	const { account } = useAccount();
	const { navigationConfig } = useNavigationConfig();
	const headerRef = useRef<HTMLElement>(null);
	const [menuOpen, setMenuOpen] = useState(false);
	const { pathname } = useLocation();

	const solutionIds = new Set();
	const solutions = (account?.programs ?? []).flatMap((p) => p.solutions);
	const uniqueSolutions = solutions
		.filter(({ solutionId }) => !solutionIds.has(solutionId) && solutionIds.add(solutionId))
		.sort((a, b) => a.solutionName.localeCompare(b.solutionName, undefined, { sensitivity: 'base' }));
	const solutionsAsNavLinks = useMemo(
		() =>
			uniqueSolutions.map<NaviationSectionLink>((s) => ({
				external: true,
				to: '',
				title: s.solutionName,
				description: s.solutionDescription,
				onClick: async (event) => {
					event.preventDefault();

					try {
						const token = await encryptService.getEncryptedToken().fetch();
						window.location.href = constructUrl(s.solutionUrl, {
							'X-Hackett-Access-Token': token,
						});
					} catch (error) {
						handleError(error);
					}
				},
			})),
		[handleError, uniqueSolutions]
	);

	const navigationLinks: NavigationLinks = useMemo(() => {
		return {
			MAIN: [
				...(!navigationConfig?.inquiriesDisabled && !navigationConfig?.allowClientManagement
					? [
							{
								to: navigationConfig?.allowAccessToInquiries ? '/inquiries' : '/inquiries/learn-more',
								title: 'Expert<br/>Inquiries',
								mobileTitle: 'Expert Inquiries',
							},
					  ]
					: []),
				...(navigationConfig?.allowResearch ? [{ to: '/research', title: 'Research' }] : []),
				...(navigationConfig?.allowPerformanceMetrics
					? [
							{
								to: '/performance-metrics',
								title: 'Benchmark<br/>Metrics',
								mobileTitle: 'Benchmark Metrics',
							},
					  ]
					: []),
				...(navigationConfig?.allowBestPractices
					? [
							{
								to: '/best-practices',
								title:
									'<span class="d-block">Hackett-Certified<sup>&reg;</sup></span><span class="d-block">Best Practices</span>',
								mobileTitle: 'Hackett-Certified<sup>&reg;</sup> Best Practices',
							},
					  ]
					: []),
				...(navigationConfig?.allowPerformanceStudies
					? [
							{
								to: '/events/performance-studies',
								title: 'Performance<br/>Studies',
								mobileTitle: 'Performance Studies',
							},
					  ]
					: []),
				...(navigationConfig?.allowBrowseEvents ? [{ to: '/events/browse-events', title: 'Events' }] : []),
				...(navigationConfig?.allowUseCases ? [{ to: '/use-cases', title: 'AI Use Cases' }] : []),
				...(navigationConfig?.allowTools ? [{ to: '/tools', title: 'Accelerators' }] : []),
				...(navigationConfig?.allowHackettAi
					? [{ to: '/ask-hackett', title: '<span class="d-block">AskHackett&trade;</span>' }]
					: []),
			],
			SPEND_MATTERS: solutionsAsNavLinks,
			CLIENT_MANAGEMENT: navigationConfig?.allowClientManagement
				? [
						...(navigationConfig?.allowAccessToInquiries
							? [
									{
										to: '/client-management/inquiries-dashboard',
										title: 'Inquiries',
									},
							  ]
							: []),
						...(navigationConfig?.allowDeliverables
							? [
									{
										to: '/client-management/deliverables',
										title: 'Deliverables',
									},
							  ]
							: []),
						...(navigationConfig?.allowReports
							? [{ to: '/client-management/reports', title: 'Reports' }]
							: []),
				  ]
				: [],
			ACCOUNT: [
				...(navigationConfig?.allowAdminAccess
					? [
							{
								to: navigationConfig?.adminUrl ?? '',
								title: 'Admin',
								external: true,
							},
					  ]
					: []),
				{ to: '/account', title: 'Account' },
				{ to: '/bookmarks', title: 'Bookmarks' },
				...(navigationConfig?.allowBlogs ? [{ to: '/blog-posts', title: 'Blog Posts' }] : []),
			],
		};
	}, [navigationConfig, solutionsAsNavLinks]);

	/* ----------------------------------------------------------- */
	/* Banner messages */
	/* ----------------------------------------------------------- */
	const [bannerMessages, setBannerMessages] = useState<BannerMessage[]>([]);
	const bannerMessagesFetched = useRef(false);

	const fetchBannerMessages = useCallback(async () => {
		try {
			if (bannerMessagesFetched.current) {
				return;
			}

			const [bannerMessagesResponse] = await Promise.all([accountService.getBannerMessages().fetch()]);

			setBannerMessages(bannerMessagesResponse.bannerMessages);
			bannerMessagesFetched.current = true;
		} catch (error) {
			handleError(error);
		}
	}, [handleError]);

	useEffect(() => {
		fetchBannerMessages();
	}, [fetchBannerMessages]);

	/* ----------------------------------------------------------- */
	/* Resize Handler */
	/* ----------------------------------------------------------- */
	const handleWindowResize = useCallback(() => {
		setBodyPadding();
	}, []);

	/* ----------------------------------------------------------- */
	/* Body padding for fixed header */
	/* ----------------------------------------------------------- */
	function setBodyPadding() {
		if (!headerRef.current) {
			document.body.style.paddingTop = '0px';
			return;
		}

		const headerHeight = headerRef.current.clientHeight;
		document.body.style.paddingTop = `${headerHeight}px`;
	}

	/* ----------------------------------------------------------- */
	/* Set padding on window resize, bind handler */
	/* ----------------------------------------------------------- */
	useEffect(() => {
		setBodyPadding();

		window.addEventListener('resize', handleWindowResize);
		return () => {
			window.removeEventListener('resize', handleWindowResize);
			document.body.style.paddingTop = '0px';
		};
	}, [handleWindowResize]);

	/* ----------------------------------------------------------- */
	/* Set padding if bannerMessages changes */
	/* ----------------------------------------------------------- */
	useEffect(() => {
		setBodyPadding();
	}, [bannerMessages]);

	/* ----------------------------------------------------------- */
	/* Disable scrolling when menu is open */
	/* ----------------------------------------------------------- */
	useEffect(() => {
		if (menuOpen) {
			document.body.style.overflow = 'hidden';
			return;
		}

		document.body.style.overflow = 'visible';

		return () => {
			document.body.style.overflow = 'visible';
		};
	}, [menuOpen]);

	/* ----------------------------------------------------------- */
	/* Close menu if pathname changes */
	/* ----------------------------------------------------------- */
	useEffect(() => {
		setMenuOpen((previousValue) => {
			if (previousValue) {
				return false;
			}

			return previousValue;
		});

		document.body.style.overflow = 'visible';
	}, [pathname]);

	return (
		<>
			<HeaderMenu show={menuOpen} onHide={() => setMenuOpen(!menuOpen)} navigationLinks={navigationLinks} />

			<header ref={headerRef} className={classes.header}>
				{bannerMessages.length > 0 && (
					<WelcomeBanner bannerMessages={bannerMessages} onBannerMessageRead={setBannerMessages} />
				)}
				<HeaderDesktop
					menuOpen={menuOpen}
					onMenuButtonClick={() => setMenuOpen(!menuOpen)}
					navigationLinks={navigationLinks}
					className={classes.headerDesktop}
				/>
				<HeaderMobile
					menuOpen={menuOpen}
					onMenuButtonClick={() => setMenuOpen(!menuOpen)}
					className={classes.headerMobile}
				/>
				<NavigationMain navigationLinks={navigationLinks} className={classes.headerDesktop} />
			</header>
		</>
	);
};
